import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    CircularProgress,
    Chip,
    Alert,
} from "@mui/material";
import { Mic, Stop, Send } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import DoctorSlotsDisplay from "./DoctorSlotsDisplay";
import ErrorBoundary from "./ErrorBoundary";
import "./VoiceAssistant.scss";

const LANGUAGES = [
    { code: "en", name: "English" },
    { code: "hi", name: "Hindi" },
    { code: "bn", name: "Bengali" },
    { code: "as", name: "Assamese" },
    { code: "gu", name: "Gujarati" },
    { code: "kn", name: "Kannada" },
    { code: "ml", name: "Malayalam" },
    { code: "pa", name: "Punjabi" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "od", name: "Odia" },
    { code: "mix", name: "Mixed Languages" },
];

const VoiceAssistant = ({ patientId = "PAT-848", orgCode, appMode, onClose }) => {
    const [selectedLanguage, setSelectedLanguage] = useState("en");
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [assessmentData, setAssessmentData] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [showDoctors, setShowDoctors] = useState(false);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);

    const REACT_APP_BACKEND_ROUTE_URL =
        process.env.REACT_APP_BACKEND_ROUTE_URL;

    useEffect(() => {
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [audioUrl]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                setAudioBlob(blob);
                const url = URL.createObjectURL(blob);
                setAudioUrl(url);
                stream.getTracks().forEach((track) => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);

            timerRef.current = setInterval(() => {
                setRecordingTime((prev) => prev + 1);
            }, 1000);
        } catch (error) {
            console.error("Error accessing microphone:", error);
            toast.error("Unable to access microphone. Please check permissions.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
    };

    const handleSendAssessment = async () => {
        if (!audioBlob) {
            toast.error("Please record your voice first");
            return;
        }

        setIsProcessing(true);
        try {
            const formData = new FormData();
            formData.append("file", audioBlob, "recording.webm");
            formData.append("language", selectedLanguage);
            formData.append("username", "reed");
            formData.append("password", "Test@123");

            // Build headers with orgCode and appMode from props or sessionStorage
            const headers = {};
            if (orgCode) {
                headers["Org-Code"] = orgCode;
            }
            if (appMode) {
                headers["App-Mode"] = appMode;
            }

            // Don't set Content-Type manually - axios will set it with boundary for FormData
            const response = await axios.post(
                `${REACT_APP_BACKEND_ROUTE_URL}patient_voice_assessment/`,
                formData,
                {
                    headers: {
                        "Org-Code": "ORGCPMHG",
                        "App-Mode": "SaaS"
                    }
                }
            );

            if (response?.data?.status === "Success") {
                console.log("response?.data?.data", response?.data?.data);
                // Set state directly - ErrorBoundary will catch any rendering errors
                setAssessmentData(response?.data || {});
                setShowResults(true);
                // Defer showing doctors section to avoid theme errors
                setTimeout(() => {
                    setShowDoctors(true);
                }, 100);
                toast.success("Assessment completed successfully!");
            } else {
                toast.error(response?.data?.message || "Failed to get assessment. Please try again.");
            }
        } catch (error) {
            console.error("Error sending voice assessment:", error);
            const errorMessage = error?.response?.data?.message ||
                error?.message ||
                "Unable to process voice assessment. Please try again.";
            toast.error(errorMessage);
            setIsProcessing(false);
        }
    };

    const handleReset = () => {
        setAudioBlob(null);
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
        }
        setAudioUrl(null);
        setAssessmentData(null);
        setShowResults(false);
        setShowDoctors(false);
        setRecordingTime(0);
    };

    const handleNewRecording = () => {
        handleReset();
    };

    return (
        <Box className="voice-assistant-container">
            <Card className="voice-assistant-card">
                <CardContent>
                    <Typography variant="h5" className="voice-assistant-title" gutterBottom>
                        Voice Health Assessment
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Describe your symptoms or chief complaint in your preferred language
                    </Typography>

                    {!showResults ? (
                        <>
                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>Select Language</InputLabel>
                                <Select
                                    value={selectedLanguage}
                                    onChange={(e) => setSelectedLanguage(e.target.value)}
                                    label="Select Language"
                                    disabled={isRecording || isProcessing}
                                >
                                    {LANGUAGES.map((lang) => (
                                        <MenuItem key={lang.code} value={lang.code}>
                                            {lang.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box className="recording-section">
                                <Box className="recording-controls">
                                    {!isRecording && !audioBlob ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            startIcon={<Mic />}
                                            onClick={startRecording}
                                            disabled={isProcessing}
                                            className="record-button"
                                        >
                                            Start Recording
                                        </Button>
                                    ) : isRecording ? (
                                        <Button
                                            variant="contained"
                                            color="error"
                                            size="large"
                                            startIcon={<Stop />}
                                            onClick={stopRecording}
                                            className="stop-button"
                                        >
                                            Stop Recording ({formatTime(recordingTime)})
                                        </Button>
                                    ) : (
                                        <Box className="audio-preview-section">
                                            <Box className="audio-controls">
                                                {audioUrl && (
                                                    <audio
                                                        controls
                                                        src={audioUrl}
                                                        style={{ width: "100%", marginBottom: "16px" }}
                                                    />
                                                )}
                                                <Box className="action-buttons">
                                                    <Button
                                                        variant="outlined"
                                                        onClick={handleNewRecording}
                                                        disabled={isProcessing}
                                                        sx={{ mr: 2 }}
                                                    >
                                                        Record Again
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        startIcon={<Send />}
                                                        onClick={handleSendAssessment}
                                                        disabled={isProcessing}
                                                    >
                                                        {isProcessing ? (
                                                            <>
                                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                                Processing...
                                                            </>
                                                        ) : (
                                                            "Send for Assessment"
                                                        )}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <AssessmentResults
                            data={assessmentData.data}
                            patientId={patientId}
                            onNewRecording={handleNewRecording}
                            showDoctors={showDoctors}
                            setShowDoctors={setShowDoctors}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

const AssessmentResults = ({ data, patientId, onNewRecording, showDoctors, setShowDoctors }) => {
    // Set showDoctors if available_doctors exist and showDoctors is not yet set
    React.useEffect(() => {
        if (data && data.available_doctors && Array.isArray(data.available_doctors) && data.available_doctors.length > 0 && !showDoctors && setShowDoctors) {
            setTimeout(() => {
                setShowDoctors(true);
            }, 100);
        }
    }, [data, data?.available_doctors, showDoctors, setShowDoctors]);

    if (!data || typeof data !== 'object') {
        return null;
    }

    return (
        <Box className="assessment-results">
            {/* <Box className="results-header">
                <Typography variant="h6" gutterBottom>
                    Assessment Results
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onNewRecording}
                    sx={{ mb: 2 }}
                >
                    New Assessment
                </Button>
            </Box> */}

            {data.chief_complaints && data.chief_complaints.length > 0 && (
                <Box className="result-section" sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Chief Complaints
                    </Typography>
                    <Box className="chips-container">
                        {data.chief_complaints.map((complaint, index) => (
                            <Chip
                                key={index}
                                label={complaint}
                                color="primary"
                                variant="outlined"
                                sx={{ mr: 1, mb: 1 }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {data.recommended_specialization && (
                <Alert
                    severity="info"
                    sx={{ mb: 3 }}
                    onClose={() => { }}
                >
                    <Typography variant="subtitle2" fontWeight="bold">
                        Recommended Specialization: {data.recommended_specialization}
                    </Typography>
                </Alert>
            )}

            {data.risk_assessment && (
                <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Risk Assessment
                    </Typography>
                    {(data.risk_assessment.risk_level || data.risk_assessment.risk_percentage !== undefined) && (
                        <Typography variant="body2" color="text.secondary" textAlign="justify">
                            Risk Level: <strong>{data.risk_assessment.risk_level || "N/A"}</strong>
                            {data.risk_assessment.risk_percentage !== undefined && (
                                <> ({data.risk_assessment.risk_percentage}%)</>
                            )}
                        </Typography>
                    )}
                    {data.risk_assessment.explanation && (
                        <Typography variant="body2" sx={{ mt: 1 }} textAlign="justify">
                            {data.risk_assessment.explanation}
                        </Typography>
                    )}
                </Card>
            )}

            {data.consultation_urgency && data.consultation_urgency.urgency_level && (
                <Alert
                    severity={
                        String(data.consultation_urgency.urgency_level || "").includes("24 hours")
                            ? "warning"
                            : "info"
                    }
                    sx={{ mb: 3 }}
                    onClose={() => { }}
                >
                    <Typography variant="subtitle2" fontWeight="bold">
                        Consultation Urgency: {data.consultation_urgency.urgency_level}
                    </Typography>
                    {data.consultation_urgency.reason && (
                        <Typography variant="body2" sx={{ mt: 1 }} textAlign="justify">
                            {data.consultation_urgency.reason}
                        </Typography>
                    )}
                </Alert>
            )}

            {data.home_treatments && data.home_treatments.length > 0 && (
                <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Home Treatments
                    </Typography>
                    {data.home_treatments.map((treatment, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight="medium" textAlign="justify">
                                {treatment.treatment}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="justify">
                                {treatment.instructions}
                            </Typography>
                        </Box>
                    ))}
                </Card>
            )}

            {data.warning_signs && Array.isArray(data.warning_signs) && data.warning_signs.length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => { }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Warning Signs - Seek Immediate Medical Attention
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {data.warning_signs.map((sign, index) => (
                            <li key={index}>
                                <Typography variant="body2" textAlign="justify">{sign}</Typography>
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            {showDoctors && data.available_doctors && Array.isArray(data.available_doctors) && data.available_doctors.length > 0 && (
                <Box className="doctors-section">
                    <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                        Available Doctors
                    </Typography>
                    <ErrorBoundary>
                        <DoctorSlotsDisplay
                            doctors={data.available_doctors}
                            job_id={data.job_id}
                            patientId={patientId}
                            specialization={data.recommended_specialization}
                        />
                    </ErrorBoundary>
                </Box>
            )}

            {data.disclaimer && (
                <Alert severity="info" sx={{ mt: 3 }} onClose={() => { }}>
                    <Typography variant="caption" textAlign="justify">{data.disclaimer}</Typography>
                </Alert>
            )}
        </Box>
    );
};

export default VoiceAssistant;

